import { apiErrorSchema } from '@/types/api'
import { getAppConfig } from '@/config/env'
import { getStoredToken } from '@/auth/tokenStorage'

export class ApiClientError extends Error {
  readonly status: number
  readonly code: string

  constructor(message: string, status: number, code = 'request_failed') {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** When false, omit X-API-Key even if configured (public endpoints). */
  authenticated?: boolean
  /** When false, do not attach Bearer JWT. */
  bearer?: boolean
}

function buildHeaders(options: RequestOptions): Headers {
  const config = getAppConfig()
  const headers = new Headers({
    Accept: 'application/json',
  })

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }

  const useApiKey = options.authenticated !== false
  if (useApiKey && config.apiKey) {
    headers.set('X-API-Key', config.apiKey)
  }

  const useBearer = options.bearer !== false
  const token = getStoredToken()
  if (useBearer && token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return headers
}

/**
 * Typed fetch wrapper for the Oikos Market Flask API.
 */
export async function apiRequest<T>(
  path: string,
  parse: (data: unknown) => T,
  options: RequestOptions = {},
): Promise<T> {
  const config = getAppConfig()
  const url = `${config.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers: buildHeaders(options),
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    mode: 'cors',
  })

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload: unknown = isJson ? await response.json() : undefined

  if (!response.ok) {
    const parsedError = apiErrorSchema.safeParse(payload)
    if (parsedError.success) {
      throw new ApiClientError(
        parsedError.data.error.message,
        response.status,
        parsedError.data.error.code,
      )
    }
    throw new ApiClientError(
      `Request failed with status ${response.status}`,
      response.status,
    )
  }

  try {
    return parse(payload)
  } catch {
    throw new ApiClientError('Received an unexpected response shape.', 502, 'invalid_response')
  }
}

/** Download a binary response (e.g. PDF / proof of payment) with auth headers. */
export async function apiDownload(
  path: string,
  filename: string,
  accept = 'application/pdf,*/*',
): Promise<void> {
  const config = getAppConfig()
  const url = `${config.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`
  const headers = buildHeaders({ authenticated: true, bearer: true })
  headers.set('Accept', accept)

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    mode: 'cors',
  })

  if (!response.ok) {
    throw new ApiClientError(`Download failed with status ${response.status}`, response.status)
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

/** Multipart upload helper (do not set Content-Type — browser sets the boundary). */
export async function apiUploadFormData<T>(
  path: string,
  formData: FormData,
  parse: (data: unknown) => T,
): Promise<T> {
  const config = getAppConfig()
  const url = `${config.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`
  const headers = buildHeaders({ authenticated: true, bearer: true })
  headers.delete('Content-Type')

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    mode: 'cors',
  })

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload: unknown = isJson ? await response.json() : undefined

  if (!response.ok) {
    const parsedError = apiErrorSchema.safeParse(payload)
    if (parsedError.success) {
      throw new ApiClientError(
        parsedError.data.error.message,
        response.status,
        parsedError.data.error.code,
      )
    }
    throw new ApiClientError(`Upload failed with status ${response.status}`, response.status)
  }

  try {
    return parse(payload)
  } catch {
    throw new ApiClientError('Received an unexpected response shape.', 502, 'invalid_response')
  }
}

